import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../lib/utils';

const buttonVariants = cva(
	'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				default: 'bg-neutral-950 text-white hover:bg-neutral-800',
				secondary: 'bg-neutral-100 text-neutral-900 hover:bg-neutral-200',
				outline: 'border border-neutral-200 bg-transparent text-neutral-900 hover:bg-neutral-50',
				ghost: 'text-neutral-900 hover:bg-neutral-100',
				accent: 'bg-brand-accent text-white hover:bg-brand-accent/90',
			},
			size: {
				default: 'h-10 px-4 py-2',
				sm: 'h-9 rounded-md px-3',
				lg: 'h-11 rounded-md px-8',
				icon: 'h-10 w-10',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />
		);
	},
);
Button.displayName = 'Button';

export { Button, buttonVariants };
